import { useCreateMandateTypeFromCheckpointIds } from "./useCreateMandateTypeFromCheckpointIds";
import { useCreateMandateForChecks } from "./useCreateMandateForChecks";
import { useCreateIndividual } from "./useCreateIndividual";
import { useCreateEntity } from "./useCreateEntity";
import { ScreeningSubject } from "../utils/screeningChecks";

/** Which call in the chain failed, so the caller can word its own error. */
export type ScreeningRecordStage = "mandateType" | "mandate" | "subject";

interface CreateParams {
  checkpointIds: number[];
  subject: ScreeningSubject;
  /** The subject payload, less `mandate`, which this hook fills in. */
  payload: any;
  profileId?: number;
  primaryDivision?: number;
  primaryTeam?: number;
  /** Ids of Divisions/Teams the record is also shared into. */
  additionalDivisions?: number[];
  additionalTeams?: number[];
}

interface CreateCallbacks {
  onSuccess: (result: { mandateId: number; recordId: number }) => void;
  onError: (err: any, stage: ScreeningRecordStage) => void;
}

/**
 * Creates an ad-hoc screening record: a throwaway MandateType from the selected
 * checkpoint identifiers, a Mandate in the Checks category, then the individual
 * or entity being screened.
 *
 * The three screening wizards ran this chain independently, which is why the
 * check name lists and the guards had drifted apart between them.
 */
export const useCreateScreeningRecord = () => {
  const { mutate: createMandateType, isLoading: isCreatingMandateType } =
    useCreateMandateTypeFromCheckpointIds();
  const { mutate: createMandate, isLoading: isCreatingMandate } =
    useCreateMandateForChecks();
  const { mutate: createIndividual, isLoading: isCreatingIndividual } =
    useCreateIndividual();
  const { mutate: createEntity, isLoading: isCreatingEntity } =
    useCreateEntity();

  const createScreeningRecord = (
    {
      checkpointIds,
      subject,
      payload,
      profileId,
      primaryDivision,
      primaryTeam,
      additionalDivisions,
      additionalTeams,
    }: CreateParams,
    { onSuccess, onError }: CreateCallbacks
  ) => {
    createMandateType(
      { checkpoint: checkpointIds },
      {
        onSuccess: (res: any) => {
          const mandateTypeId = res?.data?.id;
          if (!mandateTypeId) {
            onError(res, "mandateType");
            return;
          }

          createMandate(
            {
              mandate_type: mandateTypeId,
              category: "Checks",
              primary_division: primaryDivision,
              primary_team: primaryTeam,
              additional_divisions: additionalDivisions,
              additional_teams: additionalTeams,
            },
            {
              onSuccess: ({ data: mandate }: any) => {
                if (!mandate?.id) {
                  onError(mandate, "mandate");
                  return;
                }

                const subjectPayload = {
                  ...payload,
                  mandate: mandate.id,
                  ...(profileId ? { profile: profileId } : {}),
                } as any;

                const subjectCallbacks = {
                  onSuccess: ({ data: record }: any) =>
                    onSuccess({ mandateId: mandate.id, recordId: record?.id }),
                  onError: (err: any) => onError(err, "subject"),
                };

                if (subject === "entity") {
                  createEntity(subjectPayload, subjectCallbacks);
                } else {
                  createIndividual(subjectPayload, subjectCallbacks);
                }
              },
              onError: (err: any) => onError(err, "mandate"),
            }
          );
        },
        onError: (err: any) => onError(err, "mandateType"),
      }
    );
  };

  return {
    createScreeningRecord,
    isLoading:
      isCreatingMandateType ||
      isCreatingMandate ||
      isCreatingIndividual ||
      isCreatingEntity,
  };
};
